import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../components/ui/dialog";
import { currency, fmtTimeAgo } from "../lib/format";
import {
    Wallet,
    Zap,
    Bot,
    ShieldCheck,
    Landmark,
    PlusCircle,
    Pencil,
    Activity,
    CheckCircle2,
    AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

function Tile({ icon: Icon, label, value, target, pct, tone = "primary", testid }) {
    const barColor =
        tone === "urgent"
            ? "bg-[var(--rl-urgent)]"
            : tone === "success"
            ? "bg-[var(--rl-success)]"
            : "bg-[var(--rl-primary)]";
    return (
        <div className="card-warm rounded-2xl p-6" data-testid={testid}>
            <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full ${barColor} text-white flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="eyebrow">{label}</div>
            </div>
            <div className="mt-4 flex items-baseline justify-between gap-2">
                <div className="font-serif text-4xl">{value}</div>
                <div className="text-sm text-[var(--rl-text-3)]">of {target}</div>
            </div>
            <div className="mt-3 h-2.5 rounded-full bg-[var(--rl-cream)] overflow-hidden">
                <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-2 text-xs text-[var(--rl-text-3)] uppercase tracking-widest">{pct}% funded</div>
        </div>
    );
}

export default function OwnerStatus() {
    const { user, loading: authLoading } = useAuth();
    const [line, setLine] = useState(null);
    const [loading, setLoading] = useState(true);
    const [depositOpen, setDepositOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    // Deposit form
    const [depAmount, setDepAmount] = useState("");
    const [depNote, setDepNote] = useState("");
    const [depSource, setDepSource] = useState("chase");

    // Edit form
    const [editCredits, setEditCredits] = useState("");
    const [editChecking, setEditChecking] = useState("");
    const [editCheckingTarget, setEditCheckingTarget] = useState("");
    const [editCreditsTarget, setEditCreditsTarget] = useState("");

    async function load() {
        try {
            const { data } = await api.get("/accounting/line");
            setLine(data);
        } catch (e) {
            if (e?.response?.status !== 401 && e?.response?.status !== 403) {
                toast.error("Could not load accounting line");
            }
        }
        setLoading(false);
    }

    useEffect(() => {
        if (authLoading) return;
        if (user?.role === "admin") load();
        else setLoading(false);
    }, [user, authLoading]);

    if (authLoading) return <div className="p-16 text-center text-[var(--rl-text-2)]">Loading…</div>;
    if (!user) return <Navigate to="/auth?next=/owner/status" replace />;
    if (user.role !== "admin") {
        return (
            <div className="max-w-[600px] mx-auto px-6 py-24 text-center" data-testid="owner-status-forbidden">
                <div className="w-16 h-16 rounded-full bg-[var(--rl-urgent)] text-white flex items-center justify-center mx-auto">
                    <ShieldCheck className="w-8 h-8" />
                </div>
                <h1 className="font-serif text-3xl mt-6">Owner access only</h1>
                <p className="text-[var(--rl-text-2)] mt-3">
                    The Status Dashboard is restricted to admin accounts.
                </p>
            </div>
        );
    }
    if (loading || !line) return <div className="p-16 text-center text-[var(--rl-text-2)]">Loading owner dashboard…</div>;

    const snacksActive = line.robot_snacks_active;

    async function submitDeposit() {
        const amt = parseFloat(depAmount);
        if (!amt || amt <= 0) return toast.error("Enter a positive deposit amount");
        setBusy(true);
        try {
            await api.post("/accounting/deposit", { amount: amt, note: depNote || null, source: depSource || "manual" });
            await load();
            toast.success(`Logged ${currency(amt)} deposit`);
            setDepAmount("");
            setDepNote("");
            setDepositOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not log deposit");
        }
        setBusy(false);
    }

    function openEdit() {
        setEditCredits(String(line.emergent_credits ?? ""));
        setEditChecking(String(line.checking_balance ?? ""));
        setEditCheckingTarget(String(line.checking_target ?? ""));
        setEditCreditsTarget(String(line.credits_target ?? ""));
        setEditOpen(true);
    }

    async function submitEdit() {
        setBusy(true);
        try {
            const body = {};
            if (editCredits !== "") body.emergent_credits = parseFloat(editCredits);
            if (editChecking !== "") body.checking_balance = parseFloat(editChecking);
            if (editCheckingTarget !== "") body.checking_target = parseFloat(editCheckingTarget);
            if (editCreditsTarget !== "") body.credits_target = parseFloat(editCreditsTarget);
            await api.put("/accounting/line", body);
            toast.success("Accounting line updated");
            setEditOpen(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update balances");
        }
        setBusy(false);
    }

    return (
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12" data-testid="owner-status-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="eyebrow inline-flex items-center gap-2">
                        <Activity className="w-3.5 h-3.5" /> Owner Status Dashboard
                    </div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight mt-2">
                        Accounting Line
                    </h1>
                    <p className="text-[var(--rl-text-2)] mt-2 max-w-xl">
                        Live watch on the checking balance and Emergent LLM credits. Robot Snacks is
                        auto-hidden from the community whenever both targets are met.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={() => setDepositOpen(true)}
                        className="btn-primary rounded-full h-11 px-5"
                        data-testid="log-deposit-btn"
                    >
                        <PlusCircle className="w-4 h-4 mr-2" /> Log Deposit
                    </Button>
                    <Button
                        onClick={openEdit}
                        variant="outline"
                        className="rounded-full h-11 px-5"
                        data-testid="edit-balances-btn"
                    >
                        <Pencil className="w-4 h-4 mr-2" /> Edit Balances
                    </Button>
                </div>
            </div>

            {/* Robot Snacks status card */}
            <div
                className={`mt-8 rounded-2xl p-6 border ${
                    snacksActive
                        ? "bg-[#fff5f3] border-[var(--rl-primary)]"
                        : "bg-[#eefaf0] border-[var(--rl-success)]"
                }`}
                data-testid="snacks-status-card"
            >
                <div className="flex items-center gap-4 flex-wrap">
                    <div
                        className={`w-14 h-14 rounded-full text-white flex items-center justify-center ${
                            snacksActive ? "bg-[var(--rl-primary)]" : "bg-[var(--rl-success)]"
                        }`}
                    >
                        {snacksActive ? <Bot className="w-7 h-7" /> : <CheckCircle2 className="w-7 h-7" />}
                    </div>
                    <div className="flex-1 min-w-[240px]">
                        <div className="eyebrow">Robot Snacks status</div>
                        <div className="font-serif text-3xl mt-1" data-testid="snacks-status-label">
                            {snacksActive ? "ACTIVE — community ask visible" : "DORMANT — hidden from community"}
                        </div>
                        <p className="text-sm text-[var(--rl-text-2)] mt-2">
                            {snacksActive
                                ? `Robot Snacks is showing on Home + /robot-snacks because ${
                                      line.checking_balance < line.checking_target
                                          ? `checking is below ${currency(line.checking_target)}`
                                          : ""
                                  }${
                                      line.checking_balance < line.checking_target &&
                                      line.emergent_credits < line.credits_target
                                          ? " and "
                                          : ""
                                  }${
                                      line.emergent_credits < line.credits_target
                                          ? `credits are below ${Math.round(line.credits_target).toLocaleString()}`
                                          : ""
                                  }.`
                                : "Both accounting targets are met. Home banner and Robot Snacks checkout are auto-hidden."}
                        </p>
                    </div>
                </div>
            </div>

            {/* Balance tiles */}
            <div className="mt-6 grid md:grid-cols-2 gap-5">
                <Tile
                    icon={Wallet}
                    label="Checking balance"
                    value={currency(line.checking_balance)}
                    target={currency(line.checking_target)}
                    pct={line.checking_pct}
                    tone={line.checking_balance < line.checking_target ? "urgent" : "success"}
                    testid="checking-tile"
                />
                <Tile
                    icon={Zap}
                    label="Emergent LLM credits"
                    value={`${Math.round(line.emergent_credits).toLocaleString()}`}
                    target={`${Math.round(line.credits_target).toLocaleString()}`}
                    pct={line.credits_pct}
                    tone={line.emergent_credits < line.credits_target ? "urgent" : "success"}
                    testid="credits-tile"
                />
            </div>

            {/* Rules explainer */}
            <div className="mt-6 card-warm rounded-2xl p-5 text-sm text-[var(--rl-text-2)]">
                <div className="flex items-start gap-3">
                    <AlertCircle className="w-4 h-4 text-[var(--rl-primary)] mt-0.5 shrink-0" />
                    <div>
                        <div className="font-semibold text-[var(--rl-text)]">Threshold rule</div>
                        <div className="mt-1">
                            Robot Snacks is <strong>active</strong> whenever
                            <span className="font-mono mx-1 px-1.5 py-0.5 bg-[var(--rl-cream)] rounded">checking &lt; {currency(line.checking_target)}</span>
                            OR
                            <span className="font-mono mx-1 px-1.5 py-0.5 bg-[var(--rl-cream)] rounded">credits &lt; {Math.round(line.credits_target).toLocaleString()}</span>.
                            It auto-hides once both are met and re-appears when either dips again.
                            Since bank transfers can&apos;t be automated, use <em>Log Deposit</em> after every Chase transfer to keep this in sync.
                        </div>
                    </div>
                </div>
            </div>

            {/* Deposit log */}
            <div className="mt-10">
                <div className="flex items-baseline justify-between">
                    <h2 className="font-serif text-2xl">Deposit log</h2>
                    <div className="text-xs text-[var(--rl-text-3)] uppercase tracking-widest">
                        {line.deposits?.length || 0} entries
                    </div>
                </div>
                <div className="mt-4 space-y-2" data-testid="deposit-log-list">
                    {(!line.deposits || line.deposits.length === 0) ? (
                        <div className="card-warm rounded-lg p-5 text-center text-sm text-[var(--rl-text-2)]">
                            No deposits logged yet. Hit <em>Log Deposit</em> after your next Chase transfer.
                        </div>
                    ) : (
                        line.deposits.map((d) => (
                            <div key={d.id} className="card-warm rounded-lg p-4 flex items-start gap-3">
                                <div className="w-9 h-9 rounded-full bg-[var(--rl-sage)] flex items-center justify-center shrink-0">
                                    <Landmark className="w-4 h-4 text-[var(--rl-text)]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                        <span className="font-semibold text-[var(--rl-primary)]">{currency(d.amount)}</span>
                                        <span className="eyebrow">{d.source}</span>
                                        <span className="text-xs text-[var(--rl-text-3)]">· {fmtTimeAgo(d.created_at)}</span>
                                    </div>
                                    {d.note && <div className="text-sm text-[var(--rl-text-2)] mt-1">{d.note}</div>}
                                    <div className="text-[10px] text-[var(--rl-text-3)] mt-0.5">Logged by {d.logged_by}</div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Deposit dialog */}
            <Dialog open={depositOpen} onOpenChange={setDepositOpen}>
                <DialogContent data-testid="deposit-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Log a bank deposit</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="eyebrow">Amount (USD)</label>
                            <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={depAmount}
                                onChange={(e) => setDepAmount(e.target.value)}
                                placeholder="e.g. 500"
                                className="mt-1.5 border-[var(--rl-border)]"
                                data-testid="deposit-amount"
                            />
                        </div>
                        <div>
                            <label className="eyebrow">Source</label>
                            <Input
                                value={depSource}
                                onChange={(e) => setDepSource(e.target.value)}
                                placeholder="chase / manual / transfer"
                                className="mt-1.5 border-[var(--rl-border)]"
                                data-testid="deposit-source"
                            />
                        </div>
                        <div>
                            <label className="eyebrow">Note (optional)</label>
                            <Textarea
                                value={depNote}
                                onChange={(e) => setDepNote(e.target.value)}
                                placeholder="e.g. Confirmation #12345 · From Stripe payout"
                                className="mt-1.5 border-[var(--rl-border)]"
                                data-testid="deposit-note"
                            />
                        </div>
                        <div className="text-xs text-[var(--rl-text-3)]">
                            This adds to the checking balance and appends an audit row. Only visible to admins.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDepositOpen(false)}>Cancel</Button>
                        <Button onClick={submitDeposit} disabled={busy} className="btn-primary" data-testid="submit-deposit-btn">
                            {busy ? "Logging…" : "Log deposit"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit balances dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent data-testid="edit-balances-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Edit accounting line</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="eyebrow">Checking balance</label>
                                <Input
                                    type="number"
                                    value={editChecking}
                                    onChange={(e) => setEditChecking(e.target.value)}
                                    className="mt-1.5 border-[var(--rl-border)]"
                                    data-testid="edit-checking"
                                />
                            </div>
                            <div>
                                <label className="eyebrow">Emergent credits</label>
                                <Input
                                    type="number"
                                    value={editCredits}
                                    onChange={(e) => setEditCredits(e.target.value)}
                                    className="mt-1.5 border-[var(--rl-border)]"
                                    data-testid="edit-credits"
                                />
                            </div>
                            <div>
                                <label className="eyebrow">Checking target</label>
                                <Input
                                    type="number"
                                    value={editCheckingTarget}
                                    onChange={(e) => setEditCheckingTarget(e.target.value)}
                                    className="mt-1.5 border-[var(--rl-border)]"
                                    data-testid="edit-checking-target"
                                />
                            </div>
                            <div>
                                <label className="eyebrow">Credits target</label>
                                <Input
                                    type="number"
                                    value={editCreditsTarget}
                                    onChange={(e) => setEditCreditsTarget(e.target.value)}
                                    className="mt-1.5 border-[var(--rl-border)]"
                                    data-testid="edit-credits-target"
                                />
                            </div>
                        </div>
                        <div className="text-xs text-[var(--rl-text-3)]">
                            Manually update credits when you top up in the Emergent portal. Checking balance is
                            better updated via <em>Log Deposit</em> so entries appear in the audit log.
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                        <Button onClick={submitEdit} disabled={busy} className="btn-primary" data-testid="submit-edit-btn">
                            {busy ? "Saving…" : "Save changes"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
